import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TicketStatusGroup } from '@nbh/shared';
import { propertyTimeZone } from '../../config/property';
import { useNow } from '../../hooks/useNow';
import { ClosedRangeControl } from './ClosedRangeControl';
import { QueueEmptyState, TruncatedNotice } from './QueueNotices';
import { QueueFilters, TicketRow } from './QueueParts';
import { QueueTabs } from './QueueTabs';
import { QUEUE_PAGE_SIZE, type QueueView, clearedChips, defaultQueueView } from './queueView';
import { useQueueList } from './useQueueList';

export interface TicketQueueProps {
  onOpen?: (ticketId: string) => void;
  initialView?: Partial<QueueView>;
}

/**
 * The work queue (SPEC.md §5.1): header, then the lifecycle tabs, then the filter chips,
 * then the jobs. Updates live as tickets arrive and change, and renders from the Dexie
 * cache with the network off — see `useQueueList`.
 *
 * Chips persist across tab switches by construction: they are a separate field of one
 * view object, and a tab tap replaces only `group`.
 */
export function TicketQueue({ onOpen, initialView }: TicketQueueProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<QueueView>(() => ({
    ...defaultQueueView(new Date(), propertyTimeZone),
    ...initialView,
  }));
  // The whole list's badge clock: one 30-second interval, cleared on unmount,
  // handed to every row. Never one timer per row.
  const now = useNow(30_000);
  const { tickets, loading, error } = useQueueList(view);
  const patch = (next: Partial<QueueView>) => setView((current) => ({ ...current, ...next }));

  return (
    <div className="flex min-h-dvh flex-col">
      <QueueTabs active={view.group} onSelect={(group) => patch({ group })} />
      <QueueFilters chips={view.chips} onChange={(chips) => patch({ chips })} />
      {view.group === TicketStatusGroup.Closed && (
        <ClosedRangeControl range={view.range} onChange={(range) => patch({ range })} />
      )}

      {error && (
        <p role="alert" className="p-4 text-sm text-red-400">
          {error}
        </p>
      )}

      {loading ? (
        <p className="p-4 text-sm text-slate-400">{t('queue.loading')}</p>
      ) : tickets.length === 0 ? (
        <QueueEmptyState view={view} onClear={() => patch({ chips: clearedChips() })} />
      ) : (
        <>
          {tickets.length >= QUEUE_PAGE_SIZE && (
            <TruncatedNotice group={view.group} limit={QUEUE_PAGE_SIZE} />
          )}
          <ul className="divide-y divide-slate-800">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <TicketRow ticket={ticket} now={now} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
